'use client';

import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import type {BundleProps} from '@/redux/reducers/typings';
import type Props from './typings';

import useBasket from '@/hooks/use-basket/use-basket';
import {getBundle} from '@/redux/actions/productActions';
import {increaseBasketCount} from '@/redux/actions/settingsActions';
import {impressions, productClick} from '@/redux/actions/trackingActions';
import {getProductAddToBundle} from '@/redux/reducers/productAddToBundleReducer';
import {selectUserSettingsData} from '@/redux/reducers/userSettingsReducer';
import constantsFactory from '@/utils/constants';

import BundleBody from './BundleBody';
import BundleFooter from './BundleFooter';
import BundleHeader from './BundleHeader';
import {StyledAddBothButton, StyledAddToBundleContainer, StyledAddToBundleSection} from './styles';

const {
  TRANSLATION: {BUTTON},
  DATA_TEST_ID,
} = constantsFactory();

/**
 * @description custom hook to track product clicks and impressions for the Add to Bundle feature.
 * @param {object|null} bundleDetails add to bundle details
 * @return {object} object containing the trackProductClick function.
 */
const useTrackingAddToBundle = (bundleDetails: BundleProps | null) => {
  const dispatch = useDispatch();
  const firedOnceRef = useRef(false);

  const trackProductClick = (productSlug: string) => {
    dispatch(productClick(`/product/${productSlug}/`, productSlug));
  };

  /**
   * @description effect to track impressions for the main product and bundle product.
   * Calls the track impressions only once when the bundleDetails are available.
   */
  useEffect(() => {
    if (!bundleDetails?.main_product && !bundleDetails?.bundle_product) return;
    if (firedOnceRef.current) return;

    const url = `${window.location.pathname + window.location.hash}`;
    const productsToTrack = [bundleDetails.main_product, bundleDetails.bundle_product].map(
      (product) => ({
        above_fold: false,
        product_slug: product.slug,
        y_pos: 0,
      }),
    );

    dispatch(impressions(url, productsToTrack));
    firedOnceRef.current = true;
  }, [bundleDetails]);

  return {trackProductClick};
};

/**
 * @description a section that allows users to add two products to the basket with a discount.
 * @param {object} props - The component props
 * @param {string} props.productSlug - The slug of the product to be added to the bundle.
 * @return {JSX.Element|null} Returns a JSX element containing the Add to Bundle section or `null` if no bundle details are available.
 */
const AddProductToBundle = ({productSlug}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const basket = useBasket();
  const {t} = useTranslation();
  const settings = useSelector(selectUserSettingsData);
  const {data: bundleDetails} = useSelector(getProductAddToBundle);
  const {trackProductClick} = useTrackingAddToBundle(bundleDetails);
  const [title, setTitle] = useState<string>(() => BUTTON.ADD_BOTH_TO_BASKET);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * @description Handles the addition of both products to the basket.
   * @note TODO: `basket.addBundle()` to be replaced by async thunk action in the future.
   * that adds both products to the basket and updates redux `state.productAddToBundle`
   * to reflect the changes in the UI.
   * - loading and error state should be from `state.productAddToBundle.status`
   */
  const handleAddBothProducts = () => {
    if (!bundleDetails) return;
    setIsLoading(true);
    setTitle(BUTTON.ADDING_TO_BASKET);
    basket.addBundle(bundleDetails.main_product, bundleDetails.bundle_product, settings.currency);
    const incrementBy = basket.contains(bundleDetails.main_product) ? 1 : 2;
    dispatch(increaseBasketCount(incrementBy));
  };

  useEffect(() => {
    dispatch(getBundle(productSlug));
  }, [productSlug]);

  if (!bundleDetails) return null;

  return (
    <StyledAddToBundleSection
      aria-label="Buy two artworks with discount section"
      data-testid={DATA_TEST_ID.ADD_PRODUCT_TO_BUNDLE_SECTION}
    >
      <div>
        <StyledAddToBundleContainer>
          <BundleHeader discountPercentage={bundleDetails.discount_percentage} />

          <BundleBody
            mainProduct={bundleDetails.main_product}
            bundleProduct={bundleDetails.bundle_product}
            handleProductClick={trackProductClick}
            bundlePrice={bundleDetails.pricing[settings.currency]?.total as string}
            bundleDiscountAmount={
              bundleDetails.pricing[settings.currency]?.discount_amount as string
            }
            currency={settings.currency}
            ctaAddButton={
              <StyledAddBothButton
                onClick={handleAddBothProducts}
                title={t(title)}
                disabled={isLoading}
                tabIndex={0}
              >
                {t(title)}
              </StyledAddBothButton>
            }
          />

          <BundleFooter
            mainProduct={{
              category: bundleDetails.main_product.category_full_name,
              artist: bundleDetails.main_product.artist_name,
              price: bundleDetails.main_product.pricing[settings.currency]?.price as string,
            }}
            bundleProduct={{
              category: bundleDetails.bundle_product.category_full_name,
              artist: bundleDetails.bundle_product.artist_name,
              price: bundleDetails.bundle_product.pricing[settings.currency]?.price as string,
            }}
          />
        </StyledAddToBundleContainer>
      </div>
    </StyledAddToBundleSection>
  );
};

export default AddProductToBundle;
